/* eslint-disable max-lines, import/max-dependencies */
import { isDefined } from "@clipboard-health/util-ts";
import { Box } from "@mui/material";
import { useSession } from "@src/appV2/Auth/api/useSession";
import { formatDollarsAsUsd } from "@src/appV2/lib/Money/utils/currency";
import { SearchInput } from "@src/appV2/redesign/components/SearchInput/SearchInput";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { type ReactNode, useCallback, useEffect, useState } from "react";

import { useGetShiftTimes } from "../../api/useGetShiftTimes";
import { useUpdateDefaultShiftTimes } from "../../api/useUpdateDefaultShiftTimes";
import { majorToMinor, minorToMajor } from "../../ShiftDetails/components/chargeRateUtils";
import { BoostIncreaseRequestModal } from "@src/appV2/redesign/SpendingControls/components/SpendingLimitIncreaseRequestModal/BoostIncreaseRequestModal";
import { useInviteBoostSpendLimit } from "@src/appV2/redesign/SpendingControls/hooks/useInviteBoostSpendLimit";

import { EditShiftTimeDialog } from "../EditShiftTimeModal/EditShiftTimeDialog";
import { APPLY_TO_OPTIONS, type EditTimeResult } from "../EditShiftTimeModal/types";
import { logShiftTimeEditSaved } from "../EditShiftTimeModal/utils/logShiftTimeEditSaved";
import { WORKER_BONUS_PERCENTAGE } from "./EditInviteChargeRateDialog";
import { useInviteFlow } from "./hooks/useInviteFlow";
import { useProfessionalSearch } from "./hooks/useProfessionalSearch";
import { InviteBlockedModal } from "./InviteBlockedModal";
import { InviteValidationResultsModal } from "./InviteValidationResultsModal";
import { InviteShiftCard } from "./InviteShiftCard";
import { PostAsOpenShiftInfoModal } from "./PostAsOpenShiftInfoModal";
import { ProfessionalList } from "./ProfessionalList";
import { type Professional, type ShiftDetails } from "./types";
import { useTu } from "@src/appV2/i18n/useTu";

interface EditedShiftTimes {
  startTime: Date;
  endTime: Date;
  qualification: string;
  timeSlot: string;
}

interface InviteDrawerContentProps {
  shiftDetails: ShiftDetails;
  /**
   * When provided, syncs time edits from an external source (e.g. PostingFormContext
   * in DailyView). The chat invite flow does not use this.
   */
  lastEditedTimes?: EditedShiftTimes;
  /**
   * When provided, the invite flow starts with this professional already selected
   * (e.g. when the invite is opened from a chat or My Professionals row).
   */
  preselectedProfessional?: Professional;
  /**
   * Optional content rendered at the top of the scrollable area, above the search
   * input. Used by the chat invite flow to host the date/time-slot picker without
   * breaking the "send button always visible" contract on short viewports.
   */
  headerContent?: ReactNode;
  /**
   * When true, dismissing the "Cannot send invites" warning also closes the
   * parent surface via `onClose`. Set on surfaces where the slot is fixed
   * (DailyView's invite drawer / bottom sheet) and the user can't recover
   * inline. Leave false (default) on surfaces where the user can pick a
   * different slot, like the chat invite dialog.
   */
  closeOnBlock?: boolean;
  /** Required when `closeOnBlock` is true. */
  onClose?: () => void;
  onSuccess: () => void;
}

export function InviteDrawerContent(props: InviteDrawerContentProps) {
  const { tu } = useTu("schedule");
  const {
    shiftDetails,
    lastEditedTimes,
    preselectedProfessional,
    headerContent,
    closeOnBlock = false,
    onClose,
    onSuccess,
  } = props;

  const { user } = useSession();
  const postToMarketplaceInfoModalState = useModalState(ModalStatus.CLOSED);
  const editTimeModalState = useModalState(ModalStatus.CLOSED);

  const [editedStartTime, setEditedStartTime] = useState<Date>(shiftDetails.startTime);
  const [editedEndTime, setEditedEndTime] = useState<Date>(shiftDetails.endTime);

  const { isQualificationSpecificEnabled } = useGetShiftTimes({
    workplaceId: shiftDetails.workplaceId,
    qualification: shiftDetails.qualification,
  });

  useEffect(() => {
    setEditedStartTime(shiftDetails.startTime);
    setEditedEndTime(shiftDetails.endTime);
  }, [shiftDetails.startTime, shiftDetails.endTime]);

  // Sync with externally edited times (e.g. from PostingFormContext in DailyView)
  useEffect(() => {
    if (
      lastEditedTimes &&
      lastEditedTimes.qualification === shiftDetails.qualification &&
      lastEditedTimes.timeSlot === shiftDetails.timeSlot
    ) {
      setEditedStartTime(lastEditedTimes.startTime);
      setEditedEndTime(lastEditedTimes.endTime);
    }
  }, [lastEditedTimes, shiftDetails.qualification, shiftDetails.timeSlot]);

  const effectiveShiftDetails: ShiftDetails = {
    ...shiftDetails,
    startTime: editedStartTime,
    endTime: editedEndTime,
  };

  const hasPreselectedProfessional = isDefined(preselectedProfessional);

  const {
    searchQuery,
    isSearching,
    allProfessionals,
    starredProfessionals,
    recentProfessionals,
    setSearchQuery,
  } = useProfessionalSearch({
    workplaceId: shiftDetails.workplaceId,
    qualification: shiftDetails.qualification,
    skip: hasPreselectedProfessional,
  });

  const {
    status,
    selectedProfessional,
    postToMarketplace,
    blockReason,
    canSendInvite,
    errorReasons,
    warningReasons,
    missingDocuments,
    errorDetail,
    startTimeError,
    errorModalState,
    blockedModalState,
    baseChargeRate,
    chargeConstraints,
    customChargeRateBoost,
    isCustomRateBoostEnabled,
    isPricingLoading,
    setCustomChargeRateBoost,
    selectProfessional,
    clearSelection,
    setPostToMarketplace,
    sendInvite,
    checkIfBlocked,
    dismissError,
  } = useInviteFlow({
    shiftDetails: effectiveShiftDetails,
    preselectedProfessional,
    onSuccess,
  });

  const { mutateAsync: updateDefaultShiftTimes } = useUpdateDefaultShiftTimes();

  const maxBoostInMinorUnits =
    isDefined(baseChargeRate) && isDefined(chargeConstraints)
      ? chargeConstraints.max.amountInMinorUnits - baseChargeRate.amountInMinorUnits
      : undefined;

  const showChargeRate = isCustomRateBoostEnabled && isDefined(selectedProfessional);

  const hourlyBonusInDollars = customChargeRateBoost ?? 0;
  const hourlyBonusInMinorUnits = majorToMinor(hourlyBonusInDollars);
  const professionalBonusInDollars =
    hourlyBonusInDollars > 0
      ? Math.floor(hourlyBonusInDollars * WORKER_BONUS_PERCENTAGE * 100) / 100
      : 0;

  const professionalBonusDisplay =
    showChargeRate && isDefined(maxBoostInMinorUnits)
      ? `${professionalBonusInDollars > 0 ? "+" : ""}${formatDollarsAsUsd(
          professionalBonusInDollars
        )} /hr`
      : undefined;

  // No `editModalState` — the side panel edits the boost inline, with no editor dialog to hide
  // behind the increase-request modal; that modal opens standalone here.
  const spendLimit = useInviteBoostSpendLimit({
    workplaceId: shiftDetails.workplaceId,
    qualification: shiftDetails.qualification,
    startTime: effectiveShiftDetails.startTime,
    endTime: effectiveShiftDetails.endTime,
    baseChargeRate,
    customChargeRateBoost,
    enabled: showChargeRate,
  });

  const isCheckingSpendLimit =
    spendLimit.isActive && hourlyBonusInDollars > 0 && spendLimit.isLoading;
  const spendLimitBlocksSend = spendLimit.exceeded || isCheckingSpendLimit;

  const handleBoostChange = useCallback(
    (boostInMinorUnits: number) => {
      const boost = minorToMajor(boostInMinorUnits);
      setCustomChargeRateBoost(boost > 0 ? boost : undefined);
    },
    [setCustomChargeRateBoost]
  );

  useEffect(() => {
    checkIfBlocked();
  }, [checkIfBlocked]);

  const handleBlockedModalClose = useCallback(() => {
    blockedModalState.closeModal();
    if (closeOnBlock) {
      onClose?.();
    }
  }, [blockedModalState, closeOnBlock, onClose]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchQuery(value);
    },
    [setSearchQuery]
  );

  const handleEditTimeSave = useCallback(
    async (result: EditTimeResult) => {
      logShiftTimeEditSaved({
        workplaceId: shiftDetails.workplaceId,
        workplaceUserId: user?._id,
        timeSlot: shiftDetails.timeSlot,
        qualification: shiftDetails.qualification,
        applyTo: result.applyTo,
      });

      setEditedStartTime(result.start);
      setEditedEndTime(result.end);

      if (isDefined(shiftDetails.timezone) && result.applyTo !== APPLY_TO_OPTIONS.THIS_SHIFT_ONLY) {
        const isApplyToAll = result.applyTo === APPLY_TO_OPTIONS.ALL_SHIFTS;
        await updateDefaultShiftTimes({
          workplaceId: shiftDetails.workplaceId,
          timeSlot: shiftDetails.timeSlot,
          startTime: result.start,
          endTime: result.end,
          qualification: isApplyToAll ? undefined : shiftDetails.qualification,
          currentQualification: isApplyToAll ? shiftDetails.qualification : undefined,
          timezone: shiftDetails.timezone,
        });
      }
    },
    [updateDefaultShiftTimes, shiftDetails, user?._id]
  );

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          {headerContent}
          {!hasPreselectedProfessional && (
            <>
              <Box
                sx={(theme) => ({
                  padding: 5,
                  position: "sticky",
                  top: 0,
                  zIndex: 1,
                  backgroundColor: theme.palette.background?.standout,
                })}
              >
                <SearchInput
                  // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional for drawer UX
                  autoFocus
                  isLoading={isSearching}
                  placeholder={tu("Search professionals...")}
                  value={searchQuery}
                  onChange={handleSearchChange}
                />
              </Box>
              <ProfessionalList
                allProfessionals={allProfessionals}
                isSearching={isSearching}
                recentProfessionals={recentProfessionals}
                searchQuery={searchQuery}
                selectedProfessional={selectedProfessional}
                starredProfessionals={starredProfessionals}
                status={status}
                onSelectProfessional={selectProfessional}
              />
            </>
          )}
        </Box>

        <Box
          sx={(theme) => ({
            padding: theme.spacing(5),
            borderTop: `1px solid ${theme.palette.border?.base}`,
            backgroundColor: theme.palette.background?.standout,
            flexShrink: 0,
            // Cap the footer so the search list above keeps ≥5 rows visible
            // after a professional is selected. Without this cap the selected
            // professional + charge-rate expanded state could push the list
            // off-screen on short viewports (ORB-1749). Sized to fit the
            // hourly-bonus stepper row without triggering the footer's own scroll.
            maxHeight: "22rem",
            overflowY: "auto",
          })}
        >
          <InviteShiftCard
            boostOverageInMinorUnits={spendLimit.overageInMinorUnits}
            endTime={editedEndTime}
            errorMessage={startTimeError}
            hourlyBonusInMinorUnits={hourlyBonusInMinorUnits}
            isBoostIncreaseRequested={spendLimit.increaseRequest.isRequested}
            isBoostOverLimit={spendLimit.exceeded}
            isChargeRateLoading={isCustomRateBoostEnabled && isPricingLoading}
            isChargeRateVisible={showChargeRate}
            isDisabled={!canSendInvite || spendLimitBlocksSend}
            isLoading={status === "sending" || status === "validating"}
            maxBoostInMinorUnits={maxBoostInMinorUnits}
            postToMarketplace={postToMarketplace}
            professionalBonusDisplay={professionalBonusDisplay}
            qualificationDisplayName={shiftDetails.qualificationDisplayName}
            selectedProfessional={selectedProfessional}
            startTime={editedStartTime}
            timeSlot={shiftDetails.timeSlot}
            timezone={shiftDetails.timezone}
            onBoostChange={handleBoostChange}
            onEditTime={() => {
              editTimeModalState.openModal();
            }}
            onInfoIconClick={() => {
              postToMarketplaceInfoModalState.openModal();
            }}
            onPostToMarketplaceChange={setPostToMarketplace}
            onRemoveProfessional={hasPreselectedProfessional ? undefined : clearSelection}
            onRequestBoostIncrease={() => {
              spendLimit.increaseRequest.open({
                overageInMinorUnits: spendLimit.overageInMinorUnits,
                currentLimitInDollars: isDefined(spendLimit.limitInMinorUnits)
                  ? minorToMajor(spendLimit.limitInMinorUnits)
                  : undefined,
                qualification: shiftDetails.qualification,
              });
            }}
            onSendInvite={sendInvite}
          />
        </Box>
      </Box>

      <PostAsOpenShiftInfoModal
        modalState={postToMarketplaceInfoModalState}
        onClose={() => {
          postToMarketplaceInfoModalState.closeModal();
        }}
      />

      {blockReason && (
        <InviteBlockedModal
          blockReason={blockReason}
          modalState={blockedModalState}
          onClose={handleBlockedModalClose}
        />
      )}

      <InviteValidationResultsModal
        detail={errorDetail}
        errorReasons={errorReasons}
        missingDocuments={missingDocuments}
        modalState={errorModalState}
        professionalName={selectedProfessional?.name}
        qualificationName={shiftDetails.qualificationDisplayName}
        shiftStartDate={shiftDetails.startTime}
        warningReasons={warningReasons}
        workplaceId={shiftDetails.workplaceId}
        onDismiss={dismissError}
        onSelectAnother={hasPreselectedProfessional ? undefined : clearSelection}
      />

      <EditShiftTimeDialog
        validatePastStartTime
        initialTime={{ start: editedStartTime, end: editedEndTime }}
        modalState={editTimeModalState}
        qualificationName={shiftDetails.qualificationDisplayName}
        showQualificationSpecificOption={isQualificationSpecificEnabled}
        timeSlot={shiftDetails.timeSlot}
        timezone={shiftDetails.timezone}
        onSave={handleEditTimeSave}
      />

      <BoostIncreaseRequestModal
        increaseRequest={spendLimit.increaseRequest}
        weekStartDate={spendLimit.weekStartDate}
        workplaceId={shiftDetails.workplaceId}
      />
    </>
  );
}
/* eslint-enable max-lines, import/max-dependencies */
